import { useCallback, useEffect, useRef, useState, type MutableRefObject } from 'react';
import { useAuthKit } from '@/contexts/AuthProvider';

/**
 * Token primitives for the SpacetimeDB builders.
 *
 * `getToken` is the builders' `withTokenProvider` callback: the SDK calls it
 * on EVERY connection attempt, including the ConnectionManager's own
 * reconnect loop, and AuthKit's `getAccessToken()` refreshes the WorkOS JWT
 * itself when it is about to expire (deduped across concurrent callers and
 * tabs). So a socket that dropped while the tab was asleep reconnects with a
 * live token no matter which path drives the attempt. When the WorkOS
 * session is dead, `getAccessToken()` rejects, the attempt fails locally
 * (no doomed 401 at the server), and `AuthProvider`'s `onRefreshFailure`
 * sends the user back through sign-in (#174).
 *
 * `refreshToken()` is the pre-rebuild hook used by the reconnect paths
 * (`useAutoReconnect.beforeReconnect`, watchdog `beforeRebuild`). It keeps
 * the last known token on failure so a network blip during refresh does not
 * blank our credentials.
 *
 * `tokenReady` is a React-state flag the provider gates on — refs alone
 * don't trigger re-renders, so without this flag the provider would build
 * connections before the first token resolved and the server would close
 * every WS with code 1006 because the `?token=…` query param was missing.
 * Reported by Cloud Lead 2026-06-27 after the connectionManager refactor
 * went out.
 */
export function useTokenRef(): {
  tokenRef: MutableRefObject<string | null>;
  getToken: () => Promise<string>;
  refreshToken: () => Promise<string | null>;
  tokenReady: boolean;
} {
  const { user, organizationId, getAccessToken } = useAuthKit();
  const tokenRef = useRef<string | null>(null);
  const [tokenReady, setTokenReady] = useState(false);

  const getToken = useCallback(async () => {
    const t = await getAccessToken();
    tokenRef.current = t;
    setTokenReady(true);
    return t;
  }, [getAccessToken]);

  // Initial fetch + refetch when the signed-in user identity changes.
  useEffect(() => {
    if (!user) {
      tokenRef.current = null;
      setTokenReady(false);
      return;
    }
    let cancelled = false;
    getToken().catch(() => {
      if (!cancelled) setTokenReady(false);
    });
    return () => {
      cancelled = true;
    };
    // organizationId: OrgScopeEnforcer's switchToOrganization re-scopes the
    // session; the cached access token is stale (wrong/no org_id claim) the
    // moment it changes, so refetch — otherwise connections keep dying with
    // the unscoped token until a watchdog reconnect forces a refresh.
  }, [user, organizationId, getToken]);

  const refreshToken = useCallback(async () => {
    if (!user) return null;
    try {
      return await getToken();
    } catch {
      return tokenRef.current;
    }
  }, [user, getToken]);

  return { tokenRef, getToken, refreshToken, tokenReady };
}
